<script setup>
import { ref, computed, onMounted, onBeforeUnmount, useTemplateRef } from 'vue';
import { useEventListener } from '@vueuse/core';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({
  containerHeight: { type: Number, default: 0 },
});

// BonBoZ: 120/80 goc danh 1/4 man hinh cho khung go — Pancake/Retion chi de mot
// dong roi TU GIAN THEO NOI DUNG. Day la bo so do cua cach do.
//
// HOP_DONG 24px = hop dong cua `p` (14px * 1.65 = 23.1px) lam tron len.
// DU_RA 4px: hop go co `overflow: auto`, thieu du 0,1px la moc ngay thanh cuon
//   dung/xuong — ma thanh cuon mac dinh cua Windows keo theo hai nut mui ten to
//   bang nua o soan. Cho du ra vai pixel thi khong bao gio cham vao nguong do.
// TRAN_TU_GIAN: qua 8 dong thi thoi gian, luc do moi cho cuon — khung soan an
//   het man hinh thi khong con doc duoc hoi thoai.
const HOP_DONG = 24;
const DU_RA = 4;
const AUTO_MAX_DONG = 8;
const DEFAULT_HEIGHT = HOP_DONG + DU_RA;
const MIN_HEIGHT = DEFAULT_HEIGHT;
const TRAN_TU_GIAN = HOP_DONG * AUTO_MAX_DONG + DU_RA;
const MIN_MESSAGES_HEIGHT = 200;
const EXPAND_RATIO = 0.5;
const RESET_DELAY_MS = 120;

const wrapperRef = useTemplateRef('wrapperRef');
const surroundingHeight = ref(0);
const editorHeight = ref(DEFAULT_HEIGHT);
const isResizing = ref(false);
const startY = ref(0);
const startHeight = ref(0);
let resetTimeoutId = null;

const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

// Measure height of elements surrounding the editor (top panel, email fields, bottom panel)
const measureSurroundingHeight = () => {
  if (wrapperRef.value) {
    surroundingHeight.value = Math.max(
      0,
      wrapperRef.value.offsetHeight - editorHeight.value
    );
  }
};

const isContainerReady = computed(() => props.containerHeight > 0);

const sizeBounds = computed(() => {
  const h = props.containerHeight;
  const s = surroundingHeight.value;
  const max = Math.max(MIN_HEIGHT, h - MIN_MESSAGES_HEIGHT - s);
  const expanded = clamp(Math.floor(h * EXPAND_RATIO - s / 2), MIN_HEIGHT, max);
  return {
    min: MIN_HEIGHT,
    max: isContainerReady.value ? max : DEFAULT_HEIGHT,
    expanded,
    default: clamp(DEFAULT_HEIGHT, MIN_HEIGHT, max),
  };
});

const clampToBounds = val =>
  clamp(val, sizeBounds.value.min, sizeBounds.value.max);

// ══════════════════════════════════════════════════════════════════════════
// BonBoZ: HOP GO TU GIAN THEO NOI DUNG.
//
// Truoc day hop go cao co dinh roi cho cuon. Voi khung mot dong thi Shift+Enter
// la mat hut dong dau — go tiep ma khong nhin thay minh vua viet gi, phai cuon
// nguoc len moi doc lai duoc. Gio moi dong them vao thi hop cao them mot nac,
// giong moi khung soan tin hien dai; het TRAN_TU_GIAN (8 dong) thi moi cho cuon.
//
// Tu gian TAT khi nguoi dung tu quyet chieu cao (keo tay cam / bam nut phong
// to) — luc do y muon cua ho de len y muon cua noi dung. Gui xong thi bat lai.
const tuDongCao = ref(true);
let khungHinhCho = null;
let quanSatNoiDung = null;
let quanSatBeNgang = null;
let beNgangCu = 0;

const timHopGo = () =>
  wrapperRef.value?.querySelector('.ProseMirror-woot-style') ?? null;

// Do chieu cao NOI DUNG chu khong phai `scrollHeight`.
//
// `scrollHeight` = max(noi dung, chieu cao hop), nen khi xoa bot dong no van tra
// ve chieu cao hop hien tai — hop phinh ra roi thi khong bao gio xep lai duoc.
// Khoang cach tu dinh con dau den day con cuoi thi luon la chieu cao that, ke ca
// khi hop dang cuon (getBoundingClientRect tra vi tri hinh hoc that).
const doCaoNoiDung = () => {
  const hop = timHopGo();
  const dau = hop?.firstElementChild;
  const cuoi = hop?.lastElementChild;
  if (!dau || !cuoi) return null;

  const kieu = getComputedStyle(hop);
  return (
    cuoi.getBoundingClientRect().bottom -
    dau.getBoundingClientRect().top +
    (parseFloat(kieu.paddingTop) || 0) +
    (parseFloat(kieu.paddingBottom) || 0)
  );
};

const capNhatTuDong = () => {
  if (!tuDongCao.value || isResizing.value) return;
  const cao = doCaoNoiDung();
  if (cao === null) return;
  const tran = Math.min(TRAN_TU_GIAN, sizeBounds.value.max);
  editorHeight.value = clamp(Math.ceil(cao) + DU_RA, MIN_HEIGHT, tran);
};

// Hop go CO DUOC PHEP CUON hay khong.
//
// Dung thanh cuon he thong la thua nhan noi dung khong vua hop. O che do tu
// gian thi hop LUON vua noi dung (tru khi da cham tran 8 dong), nen mo
// `overflow: auto` san la moi ra mot thanh cuon khong bao gio duoc dung toi —
// va tren Chrome/Windows thanh do keo theo hai nut mui ten cao 17px moi nut,
// trong o soan 28px thi chung la toan bo chieu cao.
//
// Da thu tat chung bang `::-webkit-scrollbar-button` va khong an: khi
// `scrollbar-width` cung duoc dat thi Chrome bo qua toan bo nhom
// `::-webkit-scrollbar-*`, hai luat triet tieu nhau. Chan ngay tu cho sinh ra
// thi khong phu thuoc vao quirk cua trinh duyet nao ca.
//
// Van mo cuon lai o hai truong hop noi dung THAT SU vuot hop: da cham tran tu
// gian, hoac nguoi dung tu keo hop ngan hon noi dung.
const choCuon = computed(
  () =>
    !tuDongCao.value ||
    editorHeight.value >= Math.min(TRAN_TU_GIAN, sizeBounds.value.max)
);

// Gop nhieu thay doi lien tiep vao mot lan do: go nhanh thi MutationObserver ban
// ra hang chuc su kien moi giay, do lai tung lan la ep trinh duyet tinh lai bo
// cuc tung do — go bi khuc khuyu.
const henCapNhat = () => {
  if (khungHinhCho !== null) return;
  khungHinhCho = requestAnimationFrame(() => {
    khungHinhCho = null;
    capNhatTuDong();
  });
};

const clearDragStyles = () => {
  Object.assign(document.body.style, { cursor: '', userSelect: '' });
};

const getClientY = e => (e.touches ? e.touches[0].clientY : e.clientY);

const onResizeStart = event => {
  tuDongCao.value = false; // nguoi dung tu dinh chieu cao, thoi tu gian
  editorHeight.value = clampToBounds(editorHeight.value);
  measureSurroundingHeight();
  isResizing.value = true;
  startY.value = getClientY(event);
  startHeight.value = clampToBounds(editorHeight.value);
  editorHeight.value = startHeight.value;
  Object.assign(document.body.style, {
    cursor: 'row-resize',
    userSelect: 'none',
  });
};

const onResizeMove = event => {
  if (!isResizing.value) return;
  if (event.touches) event.preventDefault();
  editorHeight.value = clampToBounds(
    startHeight.value + startY.value - getClientY(event)
  );
};

const onResizeEnd = () => {
  if (!isResizing.value) return;
  isResizing.value = false;
  clearDragStyles();
};

const resetEditorHeight = () => {
  // Ve mac dinh = tra quyen lai cho noi dung. Gui xong o soan rong nen no thu ve
  // dung mot dong; con neu con chu (nhap doi vao tay cam) thi ve dung co chu.
  tuDongCao.value = true;
  editorHeight.value = sizeBounds.value.default;
  capNhatTuDong();
};

const toggleEditorExpand = () => {
  tuDongCao.value = false; // bam nut phong to cung la tu dinh chieu cao
  editorHeight.value = clampToBounds(editorHeight.value);
  measureSurroundingHeight();
  const { expanded, max, default: defaultHeight } = sizeBounds.value;
  const isExpanded = editorHeight.value > defaultHeight;
  // If expanded is too close to default, use max so the toggle is always noticeable
  const target = expanded - defaultHeight < 100 ? max : expanded;
  editorHeight.value = isExpanded ? defaultHeight : target;
};

const handleMessageSent = () => {
  clearTimeout(resetTimeoutId);
  resetTimeoutId = setTimeout(resetEditorHeight, RESET_DELAY_MS);
};

onMounted(() => {
  emitter.on(BUS_EVENTS.MESSAGE_SENT, handleMessageSent);

  if (!wrapperRef.value) return;

  // Theo doi CA CAY chu khong rieng hop go: hop go la con cua mot component tai
  // sau (Transition + v-else-if), luc `onMounted` chay co the chua ton tai. Bat
  // o goc thi lan no xuat hien cung la mot su kien, do luon duoc ngay.
  //
  // KHONG bat `attributes`: chieu cao duoc dat bang `style` tren chinh
  // `wrapperRef`, bat thuoc tinh la tu ban ra su kien cho chinh minh -> vong lap.
  quanSatNoiDung = new MutationObserver(henCapNhat);
  quanSatNoiDung.observe(wrapperRef.value, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  // Khung hep lai thi mot dong chu co the tach thanh hai — so dong doi ma noi
  // dung khong doi, MutationObserver khong thay gi. Chi doi be NGANG moi tinh:
  // be cao thi chinh viec tu gian lam doi, nghe theo la tu kich hoat vong tron.
  quanSatBeNgang = new ResizeObserver(([o]) => {
    const beNgang = o.contentRect.width;
    if (beNgang === beNgangCu) return;
    beNgangCu = beNgang;
    henCapNhat();
  });
  quanSatBeNgang.observe(wrapperRef.value);

  henCapNhat();
});

onBeforeUnmount(() => {
  emitter.off(BUS_EVENTS.MESSAGE_SENT, handleMessageSent);
  clearTimeout(resetTimeoutId);
  quanSatNoiDung?.disconnect();
  quanSatBeNgang?.disconnect();
  if (khungHinhCho !== null) cancelAnimationFrame(khungHinhCho);
  if (isResizing.value) {
    isResizing.value = false;
    clearDragStyles();
  }
});

useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);
useEventListener(document, 'touchcancel', onResizeEnd);
useEventListener(window, 'blur', onResizeEnd);

defineExpose({ toggleEditorExpand, resetEditorHeight });
</script>

<template>
  <div
    ref="wrapperRef"
    class="relative resizable-editor-wrapper"
    :data-cuon="choCuon ? '1' : '0'"
    :style="{
      '--editor-height': editorHeight + 'px',
      '--editor-min-allowed': sizeBounds.min + 'px',
      '--editor-max-allowed': sizeBounds.max + 'px',
      '--editor-height-transition': isResizing ? 'none' : '180ms ease',
    }"
  >
    <div
      class="group absolute inset-x-0 -top-4 z-10 flex h-4 cursor-row-resize select-none items-center justify-center bg-gradient-to-b from-transparent from-10% dark:to-n-surface-1/80 to-n-surface-1/90 backdrop-blur-[0.01875rem]"
      @mousedown="onResizeStart"
      @touchstart.prevent="onResizeStart"
      @dblclick="resetEditorHeight"
    >
      <div
        class="w-8 h-0.5 mt-1 rounded-full bg-n-slate-6 group-hover:bg-n-slate-8 transition-all duration-200 motion-safe:group-hover:animate-bounce"
        :class="{ 'bg-n-slate-8 animate-bounce': isResizing }"
      />
    </div>
    <slot />
  </div>
</template>

<style lang="scss" scoped>
.resizable-editor-wrapper[data-cuon='0'] :deep(.ProseMirror-woot-style) {
  overflow-y: hidden;
}
</style>
